import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Bookmark, Search, Loader2 } from 'lucide-react';
import { marcasService } from '@/services/productos.service';
import { useAuthStore } from '@/stores/auth-store';
import { useApiError } from '@/hooks/useApiError';
import { dialogs } from '@/stores/dialog-store';

export function BrandsPage() {
  const { user } = useAuthStore();
  const { handleAuthRedirect } = useApiError();
  const [brands, setBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit modal
  const [editingBrand, setEditingBrand] = useState<any>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const loadBrands = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await marcasService.findAll(user?.company?.id || '');
      setBrands(data || []);
    } catch (err: any) {
      if (err?.response?.status === 401) { handleAuthRedirect(); return; }
      setError(err.response?.data?.message || 'Error al cargar marcas');
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.company?.id) loadBrands();
  }, [user?.company?.id]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await marcasService.create({
        companyId: user?.company?.id,
        name: newName.trim(),
        description: newDescription.trim() || undefined,
      });
      setNewName('');
      setNewDescription('');
      setShowForm(false);
      loadBrands();
    } catch {
      await dialogs.alert('Error al crear marca');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (brand: any) => {
    setEditingBrand(brand);
    setEditName(brand.name);
    setEditDescription(brand.description || '');
    setEditSaving(false);
  };

  const handleEditSubmit = async () => {
    if (!editingBrand || !editName.trim()) return;
    setEditSaving(true);
    try {
      await marcasService.update(editingBrand.id, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
      });
      setEditingBrand(null);
      loadBrands();
    } catch {
      await dialogs.alert('Error al actualizar marca');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (!(await dialogs.confirm(`¿Desactivar marca "${name}"?`, { destructive: true }))) return;
    try {
      await marcasService.deactivate(id);
      loadBrands();
    } catch {
      await dialogs.alert('Error al desactivar marca');
    }
  };

  const filtered = brands.filter((b) =>
    !search || b.name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Marcas</h1>
          <p className="text-sm text-ios-gray mt-1">{brands.length} marcas registradas</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="ios-button flex items-center gap-2">
          <Plus size={18} />
          {showForm ? 'Cancelar' : 'Nueva Marca'}
        </button>
      </div>

      {/* Create Form */}
      {showForm && (
        <div className="ios-card p-4 space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Nombre de la marca..."
              className="ios-input flex-1"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <input
              type="text"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Descripción (opcional)..."
              className="ios-input w-64"
            />
            <button
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
              className="ios-button disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Crear'}
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar marca..."
          className="ios-input w-full pl-9 h-9 text-sm"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-ios-red text-sm p-4 rounded-[12px] border border-red-100">{error}</div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-ios-gray" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="ios-card p-12 text-center">
          <Bookmark size={48} className="mx-auto text-ios-gray-4 mb-4" />
          <h3 className="text-lg font-medium text-ios-gray mb-1">
            {search ? 'Sin resultados' : 'No hay marcas'}
          </h3>
          <p className="text-sm text-ios-gray mb-4">
            {search ? 'No se encontraron marcas con ese término' : 'Comienza agregando tu primera marca'}
          </p>
        </div>
      ) : (
        <div className="ios-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Marca</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Descripción</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Productos</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {filtered.map((brand: any) => (
                  <tr key={brand.id} className="hover:bg-ios-gray-6/30 transition-colors">
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-gradient-to-br from-emerald-400/30 to-emerald-200 rounded-[10px] flex items-center justify-center flex-shrink-0">
                          <Bookmark size={16} className="text-emerald-600" />
                        </div>
                        <span className="font-medium text-ios-dark">{brand.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-ios-gray text-xs">
                      {brand.description || <span className="text-ios-gray-4">—</span>}
                    </td>
                    <td className="px-4 py-3.5 text-right text-sm font-medium">
                      {brand._count?.products || 0}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEditModal(brand)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-ios-gray hover:text-ios-blue transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeactivate(brand.id, brand.name)}
                          className="p-1.5 rounded-lg hover:bg-red-50 text-ios-gray hover:text-ios-red transition-colors"
                          title="Desactivar"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editingBrand && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setEditingBrand(null)}>
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">Editar Marca</h3>
              <button onClick={() => setEditingBrand(null)} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <span className="text-lg leading-none">×</span>
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Nombre *</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Nombre de la marca"
                  className="ios-input w-full"
                  autoFocus
                  onKeyDown={(e) => e.key === 'Enter' && handleEditSubmit()}
                />
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block font-medium">Descripción</label>
                <input
                  type="text"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Descripción opcional"
                  className="ios-input w-full"
                />
              </div>
            </div>
            <div className="p-4 border-t border-ios-gray-5 flex gap-2 justify-end">
              <button
                onClick={() => setEditingBrand(null)}
                className="px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6"
              >
                Cancelar
              </button>
              <button
                onClick={handleEditSubmit}
                disabled={editSaving || !editName.trim()}
                className="px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50"
              >
                {editSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
